import React from 'react';

import { Checkbox, Chip, TableCell, TableRow, Tooltip } from '@mui/material';

import type { CampaignScheduleImportResult } from '~/hooks/apis/offers/useImportCampaignSchedules';

import { formatDateISO, formatDateSlashesWithClockInSydney } from '~/services/TimeService';

import CampaignScheduleValuePillCell from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/CampaignScheduleValuePillCell';
import type { CampaignScheduleCandidateGroup } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleCandidateGroups';
import { getScheduleTypeLabel } from '../Detail/OfferBuilder/DealInformation/tabs/campaignSchedules/campaignScheduleHistoryDisplay';

interface Props {
  group: CampaignScheduleCandidateGroup;
  isChecked: boolean;
  isConflicting: boolean;
  isIndeterminate: boolean;
  isSelectionDisabled: boolean;
  result: CampaignScheduleImportResult | undefined;
  onToggle: () => void;
}

export default function CampaignScheduleImportCandidateRow({
  group,
  isChecked,
  isConflicting,
  isIndeterminate,
  isSelectionDisabled,
  result,
  onToggle,
}: Props) {
  const first = group.candidates[0];

  return (
    <TableRow>
      <TableCell padding="checkbox">
        <Checkbox
          checked={isChecked}
          disabled={first.isMatched || !!first.dependencyError || isSelectionDisabled}
          indeterminate={isIndeterminate}
          inputProps={{
            'aria-label': `Import ${getScheduleTypeLabel(first.type)} for ${first.brandLabel} across ${group.regions.length} region(s)`,
          }}
          onChange={onToggle}
        />
      </TableCell>
      <TableCell>{getScheduleTypeLabel(first.type)}</TableCell>
      <TableCell>{first.brandLabel}</TableCell>
      <TableCell>
        <CampaignScheduleValuePillCell label="regions" values={group.regions} />
      </TableCell>
      <TableCell>
        {first.conflictingSchedule ? (
          <Tooltip
            arrow
            title={`${formatDateSlashesWithClockInSydney(first.conflictingSchedule.start)} – ${formatDateSlashesWithClockInSydney(first.conflictingSchedule.end)}`}
          >
            <span>
              {formatDateISO(first.conflictingSchedule.start)} – {formatDateISO(first.conflictingSchedule.end)}
            </span>
          </Tooltip>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        <Tooltip
          arrow
          title={`${formatDateSlashesWithClockInSydney(first.start)} – ${formatDateSlashesWithClockInSydney(first.end)}`}
        >
          <span>
            {formatDateISO(first.start)} – {formatDateISO(first.end)}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell>
        {result ? (
          <Chip
            color={result.success ? 'success' : 'error'}
            label={result.success ? (first.conflictingSchedule ? 'Updated' : 'Imported') : result.error}
            size="small"
            variant="outlined"
          />
        ) : first.isMatched ? (
          <Chip color="success" label="Already exists" size="small" variant="outlined" />
        ) : first.dependencyError ? (
          <Chip color="warning" label={first.dependencyError} size="small" variant="outlined" />
        ) : isConflicting ? (
          <Chip color="error" label="Conflicting Salesforce records" size="small" variant="outlined" />
        ) : first.conflictingSchedule ? (
          <Chip color="info" label="Will update existing schedule" size="small" variant="outlined" />
        ) : (
          <Chip color="info" label="Will create" size="small" variant="outlined" />
        )}
      </TableCell>
    </TableRow>
  );
}
